import { $API } from "boot/api";

/**
 * Fetch packages with current filters and pagination
 */
export async function fetchPackages({ commit, state }, { page = 1, perPage = null } = {}) {
  commit("setLoading", true);
  try {
    const effectivePerPage = perPage !== null ? perPage : state.pagination.perPage;
    const params = {
      page,
      per_page: effectivePerPage,
    };

    // Add filters if set
    if (state.filters.licenseId) {
      params.license_id = state.filters.licenseId;
    }
    if (state.filters.productId) {
      params.product_id = state.filters.productId;
    }
    if (state.filters.status) {
      params.status = state.filters.status;
    }
    if (state.filters.search) {
      params.search = state.filters.search;
    }

    const response = await $API.PACKAGE_MANAGEMENT.GET("packages", params);

    commit("setPackages", response.data.data || []);
    commit("setPagination", {
      currentPage: response.data.meta?.current_page || page,
      perPage: response.data.meta?.per_page || 50,
      total: response.data.meta?.total || 0,
    });

    return response.data;
  } catch (error) {
    console.error("Failed to fetch packages:", error);
    throw error;
  } finally {
    commit("setLoading", false);
  }
}

/**
 * Update filters and refetch packages
 */
export async function updateFilters({ commit, dispatch }, filters) {
  commit("setFilters", filters);
  commit("clearSelection");
  return dispatch("fetchPackages", { page: 1 });
}

/**
 * Change page and refetch packages
 */
export async function changePage({ dispatch }, page) {
  return dispatch("fetchPackages", { page });
}

/**
 * Bulk toggle exclusion for selected packages
 */
export async function bulkToggleExclusion(
  { commit, state, getters, dispatch },
  { exclude }
) {
  if (!getters.hasSelection) return;

  commit("setActionLoading", true);
  try {
    const packages = getters.selectedPackages.map((pkg) => ({
      license_id: pkg.license_id,
      tag: pkg.tag,
    }));

    const response = await $API.PACKAGE_MANAGEMENT.POST("bulk-toggle-exclusion", {
      packages,
      exclude,
    });

    // Update local state for affected packages
    packages.forEach(({ license_id, tag }) => {
      commit("updatePackageExclusionStatus", {
        licenseId: license_id,
        tag,
        isExcluded: exclude,
      });
    });

    commit("clearSelection");
    return response.data;
  } catch (error) {
    console.error("Failed to toggle exclusion:", error);
    throw error;
  } finally {
    commit("setActionLoading", false);
  }
}

/**
 * Bulk assign selected packages to a product
 */
export async function bulkAssignProduct(
  { commit, getters, dispatch },
  { productIds, productId, productName, removedFromOrigin = false }
) {
  if (!getters.hasSelection) return;

  commit("setActionLoading", true);
  try {
    const packages = getters.selectedPackages.map((pkg) => ({
      license_id: pkg.license_id,
      tag: pkg.tag,
    }));

    const response = await $API.PACKAGE_MANAGEMENT.POST("bulk-assign-product", {
      packages,
      product_ids: productIds || [productId],
      removed_from_origin: removedFromOrigin,
    });

    const createdCount = response.data.created_count || 0;
    if (createdCount > 0) {
      await dispatch("fetchPackages", { page: 1 });
    }

    commit("clearSelection");
    return response.data;
  } catch (error) {
    console.error("Failed to assign product:", error);
    throw error;
  } finally {
    commit("setActionLoading", false);
  }
}

/**
 * Update existing package mappings (edit mode)
 */
export async function updateMapping(
  { commit, dispatch },
  { tags, licenseId, productIds, removedFromOrigin }
) {
  commit("setActionLoading", true);
  try {
    const response = await $API.PACKAGE_MANAGEMENT.PUT("update-mapping", {
      tags,
      license_id: licenseId,
      product_ids: productIds,
      removed_from_origin: removedFromOrigin,
    });

    await dispatch("fetchPackages", { page: 1 });
    return response.data;
  } catch (error) {
    console.error("Failed to update mapping:", error);
    throw error;
  } finally {
    commit("setActionLoading", false);
  }
}

/**
 * Bulk remove custom mappings for selected packages
 */
export async function bulkRemoveMappings({ commit, getters, dispatch }) {
  if (!getters.hasSelection) return;

  commit("setActionLoading", true);
  try {
    const packages = getters.selectedPackages
      .filter((pkg) => pkg.custom_product_id !== null)
      .map((pkg) => ({
        license_id: pkg.license_id,
        tag: pkg.tag,
      }));

    if (packages.length === 0) {
      commit("clearSelection");
      return { count: 0, message: "No custom mappings to remove." };
    }

    const response = await $API.PACKAGE_MANAGEMENT.POST("bulk-remove-mappings", {
      packages,
    });

    // Update local state for affected packages
    packages.forEach(({ license_id, tag }) => {
      commit("removePackageCustomMapping", {
        licenseId: license_id,
        tag,
      });
    });

    commit("clearSelection");
    return response.data;
  } catch (error) {
    console.error("Failed to remove mappings:", error);
    throw error;
  } finally {
    commit("setActionLoading", false);
  }
}

/**
 * Reset store state
 */
export function resetState({ commit }) {
  commit("setPackages", []);
  commit("setPagination", { currentPage: 1, perPage: 50, total: 0 });
  commit("resetFilters");
  commit("clearSelection");
  commit("setLoading", false);
  commit("setActionLoading", false);
}

/**
 * Finish packages in bulk
 */
export async function bulkFinishPackages(
  { commit, dispatch },
  { packages },
) {
  if (!packages || packages.length === 0) {
    return;
  }

  commit( 'setActionLoading', true );

  try {
    const response = await $API.PACKAGE_MANAGEMENT.POST( 'bulk-finish', {
      packages,
    } );

    await dispatch( 'fetchPackages', { page: 1 } );
    commit( 'clearSelection' );

    return response.data;
  } catch (error) {
    console.error( 'Failed to finish packages:', error );
    throw error;
  } finally {
    commit( 'setActionLoading', false );
  }
}
